import { Gender, PetType, VehicleType } from "./enums";

export type ContractSignatureStatus = "SIGNED" | "REJECTED" | "CANCELLED";

/**
 * One failed constraint, as data rather than prose (techbase >= 1.1.0).
 *
 * The server does not know the guest's language, so it names *what* failed with a stable `code`
 * and hands over the interpolation `args`; this app owns the wording. See
 * `services/api/validationErrors.ts` for how a violation becomes a sentence.
 */
export interface ConstraintViolationApiDto {
  /** Coarse category: PATTERN | MIN | MAX | REQUIRED | UNIQUE | NOT_FOUND | FORBIDDEN. */
  type: string;
  /** Stable dotted identifier, e.g. `form.notFound` or `validation.required`. */
  code: string;
  /** Interpolation params. Structural failures (`validation.*`) always carry `field`. */
  args?: Record<string, unknown>;
}

/**
 * The error envelope this app works with.
 *
 * Deliberately tolerant of BOTH backend shapes so the frontend can deploy ahead of the backend:
 * pre-1.1.0 sends only a (English) `message`, 1.1.0+ sends `errorCode` + `constraints`. Once every
 * environment is on 1.1.0, `message`/`name`/`errorId` can go.
 *
 * Note `code` is the HTTP status and is filled in **client-side** by `formatApiError` — it is not
 * the server's error code. That lives in `errorCode`.
 */
export interface ErrorApiDto {
  /** HTTP status. Client-side only. */
  code: number;
  /** Server discriminator, e.g. `VALIDATION_ERROR`. Absent on pre-1.1.0 backends. */
  errorCode?: string;
  /** Present when `errorCode === "VALIDATION_ERROR"`. */
  constraints?: ConstraintViolationApiDto[];
  /**
   * Generic, English-only summary. Never render this to a guest — it is a developer/log string.
   * Retained as the last-resort fallback while pre-1.1.0 backends are still deployed.
   */
  message: string;
  detail?: string;
  exceptionType?: string;
  /** @deprecated Declared by the old OpenAPI schema but never actually emitted by the backend. */
  name?: string;
  /** @deprecated See {@link ErrorApiDto.name}. */
  errorId?: string;
}

export enum LanguageShortApiEnum {
  en = 'en',
  es = 'es',
  de = 'de',
  fr = 'fr',
  it = 'it'
}

export enum LanguageApiEnum {
  SPANISH = 'SPANISH',
  ENGLISH = 'ENGLISH',
  GERMAN = 'GERMAN',
  FRENCH = 'FRENCH',
  ITALIAN = 'ITALIAN'
}

export enum CountryApiEnum {
  ECUADOR = 'ECUADOR',
  USA = 'USA',
  CANADA = 'CANADA',
  UK = 'UK',
  GERMANY = 'GERMANY',
}

export enum MimeApiEnum {
  'image/jpeg' = 'image/jpeg',
  'image/png' = 'image/png'
}

export enum GuestPictureTypeApiEnum {
  ID_FRONT = 'ID_FRONT',
  ID_BACK = 'ID_BACK',
  SELFIE = 'SELFIE',
  OTHER = 'OTHER'
}

export enum IdTypeApiEnum {
  PASSPORT = 'PASSPORT',
  NATIONAL_ID = 'NATIONAL_ID'
}

/**
 * Where a guest's document was issued, relative to the LISTING's country — not to their
 * nationality, and not to the manager's own country. Migration 0160.
 *
 * Mirrors `DocumentOriginEnumDto` in guardian-shared, member for member, and the values are the
 * enum names the backend emits as the `origin` arg of `guest.idType.notAllowedForOrigin`. The
 * client derives its own direction locally today (`lockDirection`) rather than reading that arg,
 * but the two must speak one vocabulary: the moment someone wires the server's arg into copy
 * selection, a lowercase-vs-uppercase mismatch would be a silent no-match, not a type error.
 *
 * Exactly two members, deliberately. The pair is a partition — a document is either domestic or
 * foreign, never both and never neither — which is what makes the two listing flags independent
 * with no precedence between them. "We cannot tell" is not a third origin; it is the absence of a
 * comparison, and it is modelled as `null` by the functions that return this.
 */
export enum DocumentOriginApiEnum {
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN'
}

export enum FormStatusApiEnum {
  SENT = 'SENT',
  COMPLETED = 'COMPLETED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  VALIDATED = 'VALIDATED',
  INVALIDATED = 'INVALIDATED'
}

export interface PublicFormDataApiDto {
  templateId: string;
  listingTitle: string;
  listingCity: string;
  listingCountry: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
  maxVehicles: number;
  allowPets: boolean;
  listingAddress: string;
  status: FormStatusApiEnum;
  data: FormDataV1PublicApiDto;
  version: string;
  latitude?: number;
  longitude?: number;
  requiredGuestImages: GuestPictureTypeApiEnum[];
  requiredBookingContract: boolean;
  signedContract: boolean;
  minAgeToRequireImages: number;
  requireContactDataFromAllGuests: boolean;
  // Migration 0160: per-listing ID document-type restrictions, evaluated against where the
  // guest's document was ISSUED relative to `listingCountry` (already on this DTO).
  // Optional on the wire: a form-app build can be deployed ahead of the backend that emits
  // them, and `allowedIdTypes` treats undefined as false (unrestricted) — the pre-0160
  // behaviour. Client-side gating is UX; the backend re-validates at submission.
  requirePassportForForeignGuests?: boolean;
  requireNationalIdForDomesticGuests?: boolean;
  overallProgress?: number;
  contractStatus?: ContractSignatureStatus;
  rejectionReason?: string;
  // Story 28-4: full bilingual + multi-status messages map shipped on every
  // public form fetch. Always emitted (possibly `{}`). Outer key is the form
  // status (SENT, COMPLETED, ACCEPTED). Inner key is the locale (en, es).
  // Missing keys fall back to the form-app's i18n defaults at render time.
  formMessages?: Record<string, Record<string, string>>;
  brandSlug?: string | null;
  brandAutoRedirect?: boolean | null;
  // Epic 42, Story 42-15: whether the ID photo check is active on this form (the backend's
  // IdScanGate). When true, every ID_FRONT / ID_BACK upload goes through the id-scans call before it
  // is registered, and a photo the label guard rejected is refused at registration (422
  // ID_PHOTO_REJECTED). Optional on the wire: a form-app build can ship ahead of the backend that
  // emits it, and absent must mean OFF (no id-scans call at all).
  idScanEnabled?: boolean;
}

export interface FormDataV1PublicApiDto {
  mainGuest: MainGuestV1PublicApiDto;
  otherGuests: GuestV1PublicApiDto[];
  vehicles: VehicleDataWithParkingSpaceV1ApiDto[];
  pets: PetDataV1ApiDto[];
}

export interface GuestV1PublicApiDto {
  guestNumId: number;
  // Stable relational entry UUID (form_guests PK). Used to join API guests to relational
  // entries by identity instead of positional/guestNumId pairing (guest image-reuse fix, PR-4).
  guestEntryId?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: Gender;
  idType?: IdTypeApiEnum;
  id?: string;
  idIssuingCountry?: CountryApiEnum;
  telephoneNumber?: string;
  email?: string;
  birthDate?: string;
  acceptTerms?: boolean;
  acceptTermsTimestamp?: string;
  acceptPrivacy?: boolean;
  acceptPrivacyTimestamp?: string;
  consentDataStorage?: boolean;
  consentDataStorageTimestamp?: string;
  consentMarketing?: boolean;
  consentMarketingTimestamp?: string;
  files?: FileV1PublicApiDto[];
  progress?: number;
  // Story 20-1 Phase 1 (refresh-resilient prefill, supersedes Decision 23b POST-only):
  // attached by PublicApiImpl._getFormData per guest when the lookup-key triple matches
  // a non-suppressed guest_profiles row. Absent on miss/suppressed.
  profileMatch?: import("./guest").ProfileMatch | null;
}

export interface MainGuestV1PublicApiDto extends GuestV1PublicApiDto {
  email: string;
}


export interface FileV1PublicApiDto {
  type: GuestPictureTypeApiEnum;
  url?: string;
}

export interface VehicleDataV1ApiDto {
  type: VehicleType;
  model: string;
  plateNr: string;
  parkingSpace?: string;
}

export interface VehicleDataWithParkingSpaceV1ApiDto {
  type: VehicleType;
  model: string;
  plateNr: string;
  parkingSpace?: string;
  parkingRequested?: boolean;
}

export interface PetDataV1ApiDto {
  type: PetType
  race: string;
}

export interface PublicFormDataAndVersionApiDto {
  data: FormDataV1PublicApiDto;
  version: string;
}

export interface VersionApiDto {
  version: string;
}

export interface GuestAndFormVersionApiDto {
  guest: GuestV1WithoutFilesPublicApiDto;
  version: string;
}

export interface GuestV1WithoutFilesPublicApiDto {
  firstName: string;
  middleName?: string;
  lastName: string;
  gender: Gender;
  idType: IdTypeApiEnum;
  id: string;
  idIssuingCountry: CountryApiEnum;
  telephoneNumber: string;
  email: string;
  birthDate: string;
  acceptTerms?: boolean;
  acceptTermsTimestamp?: string;
  acceptPrivacy?: boolean;
  acceptPrivacyTimestamp?: string;
  consentDataStorage?: boolean;
  consentDataStorageTimestamp?: string;
  consentMarketing?: boolean;
  consentMarketingTimestamp?: string;
}

export interface VehicleAndFormVersionApiDto {
  vehicle: VehicleDataV1ApiDto;
  version: string;
}

export interface PetAndFormVersionApiDto {
  pet: PetDataV1ApiDto;
  version: string;
}

export interface PictureUploadApiDto {
  mime: MimeApiEnum;
  type: GuestPictureTypeApiEnum;
  data: string; // Base64 string (without the data: prefix)
}

export interface PictureUploadWithGuestNumIdApiDto {
  mime: MimeApiEnum;
  type: GuestPictureTypeApiEnum;
  guestNumId: number;
  data: string;
}

export interface GuestPictureIdApiDto {
  type: GuestPictureTypeApiEnum;
  guestNumId: number;
}

export interface PresignedUrlRequestApiDto {
  mime: MimeApiEnum;
  type: GuestPictureTypeApiEnum;
}

export interface PresignedUrlApiDto {
  url: string;
  key: string;
}

export interface ContentTypeApiDto {
  contentType: MimeApiEnum;
}

export interface GuestImageRegistrationApiDto {
  s3Key: string;
  type: GuestPictureTypeApiEnum;
  /**
   * Story 42-15. The scanId the id-scans call returned for THIS s3Key, when it returned one. A job
   * served from cache keeps the first upload's key, so this is how the backend finds its verdict.
   */
  scanId?: string;
}

// ---- Epic 42, Story 42-15: the ID photo check (POST/GET /v1/public/forms/{publicId}/id-scans) ----

/** `ocr_jobs.status`. PENDING, PUBLISHED and RUNNING are in flight; the other four are terminal. */
export enum IdScanStatusApiEnum {
  PENDING = 'PENDING',
  PUBLISHED = 'PUBLISHED',
  RUNNING = 'RUNNING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  TIMED_OUT = 'TIMED_OUT',
  REJECTED_NOT_ID = 'REJECTED_NOT_ID',
}

export enum IdScanCaptureApiEnum {
  CAMERA = 'CAMERA',
  FILE_PICKER = 'FILE_PICKER',
}

export interface IdScanUploadApiDto {
  /** The seat inside the key. Parsed from the key the presign returned, never from the store. */
  guestNumId: number;
  s3Key: string;
  capture?: IdScanCaptureApiEnum;
  /** Sent only when the guest has picked a type: an empty string is not a member and is a 400. */
  expectedDocumentType?: IdTypeApiEnum;
}

export interface IdScanApiDto {
  scanId: string;
  status: IdScanStatusApiEnum;
}

export interface IdScanFieldApiDto {
  field: string;
  value: string | null;
  validationStatus: string;
}

export interface IdScanResultApiDto {
  scanId: string;
  status: IdScanStatusApiEnum;
  /** Always [] unless status is SUCCEEDED. Unused by the photo check. */
  fields: IdScanFieldApiDto[];
}

export interface PersonIdApiDto {
  id: string;
  idType: IdTypeApiEnum;
  idIssuingCountry: CountryApiEnum;
}

export interface ResultApiDto {
  result: boolean;
}

export interface ContractFileDto {
  fileId: string;
  filename: string;
  url: string;
}

export interface ContractApiDto {
  generalTerms: string;
  files?: ContractFileDto[];
}

export interface ContractSignatureApiDto {
  formVersion: string;
  s3Key: string;
  contentType: MimeApiEnum;
  ipAddress: string;
  deviceInfo: string;
}

// Listing Public Properties
export interface ListingPublicPropertiesApiDto {
  title: string;
  defaultCheckinTime?: string; // example: "15:00"
  defaultCheckoutTime?: string; // example: "12:00"
  maxPeople: number;
  brandSlug?: string | null;
  brandAutoRedirect?: boolean | null;
}

// Guest Check-in Request
export interface GuestCheckinRequestApiDto {
  contactEmail: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
}

// Guest Check-in Response
export interface GuestCheckinResponseApiDto {
  publicId: string;
}

// ==================== Guest product pre-orders (story product-guest-preorder) ====================
//
// Hand-written from the backend's `Public` tag (backend.yaml): there is no codegen in this app, so
// these must be kept in step with the wire contract by hand.
//
// Money is a scale-2 decimal STRING ("35.00"), never a number, and always USD. Fields typed
// `| null` may also arrive absent; treat both the same.

/**
 * Line status as the guest sees it. NOT_CONFIRMED is computed at read time, never stored. WITHDRAWN:
 * the guest took the line back while it was waiting (spec-guest-extras-withdraw); it is final.
 */
export type StorefrontLineStatusApiEnum =
  | "REQUESTED"
  | "ACCEPTED"
  | "DECLINED"
  | "CANCELLED"
  | "WITHDRAWN"
  | "NOT_CONFIRMED";

export type ProductDeclineReasonApiEnum = "SOLD_OUT" | "OTHER";

export type ProductUnavailableReasonApiEnum = "NOT_OFFERED" | "NOT_ENOUGH_STOCK";

/** One product the guest can request right now. Absent from the list when it is not offered. */
export interface StorefrontProductApiDto {
  id: string;
  name: string;
  description?: string | null;
  photoUrl?: string | null;
  /** Tax-inclusive unit price, e.g. "35.00". */
  unitPriceGross: string;
  /**
   * Last moment to request it, as NAIVE listing-local wall time ("2026-10-18T15:00:00" or
   * "2026-10-18T15:00"). No zone: never pass it through `new Date(...)`. Present only when the
   * product has a cutoff.
   */
  orderBy?: string | null;
}

export interface StorefrontOrderLineApiDto {
  id: string;
  productName: string;
  quantity: number;
  unitPriceGross: string;
  lineTotalGross: string;
  status: StorefrontLineStatusApiEnum;
  declineReason?: ProductDeclineReasonApiEnum | null;
  declineNote?: string | null;
}

export interface StorefrontOrderApiDto {
  id: string;
  /** A real instant (TIMESTAMPTZ), e.g. "2026-10-17T22:15:03Z". */
  createdAt: string;
  /**
   * Sum of every line's lineTotalGross, whatever its status. The guest form does not show it: "Your
   * requests" sums the lines itself, by status (spec-guest-extras-joined-requests).
   */
  totalGross: string;
  lines: StorefrontOrderLineApiDto[];
}

/** GET /v1/public/forms/{publicId}/storefront */
export interface PublicProductStorefrontApiDto {
  products: StorefrontProductApiDto[];
  /** This booking's orders, newest first. */
  orders: StorefrontOrderApiDto[];
}

export interface CreateProductOrderLineApiDto {
  productId: string;
  /** 1..20 */
  quantity: number;
}

/** POST /v1/public/forms/{publicId}/product-orders body. 1..20 lines, each product once. */
export interface CreateProductOrderApiDto {
  lines: CreateProductOrderLineApiDto[];
}

/**
 * POST /v1/public/forms/{publicId}/product-orders/withdraw body (spec-guest-extras-withdraw): 1..50
 * distinct line ids, all of this booking and all still REQUESTED. All or nothing: 204, or 404
 * `ORDER_LINE_NOT_FOUND` / 409 `ORDER_LINE_NOT_REQUESTED` with nothing changed.
 */
export interface WithdrawProductOrderLinesApiDto {
  lineIds: string[];
}

export interface ProductUnavailableApiDto {
  productId: string;
  reason: ProductUnavailableReasonApiEnum;
}

/** The 409 body of a submit in which at least one line failed. Nothing was written. */
export interface ProductsUnavailableErrorApiDto {
  errorCode: "PRODUCTS_UNAVAILABLE";
  products: ProductUnavailableApiDto[];
}
